import { TreeNode } from 'primereact/treenode'
import useSWR from 'swr'

import { FetchError } from '../fetchError'
import { FetchResponse } from '../fetchResponse'
import { FetchResult } from '../fetchResult'
import {
  getContentWithBaseUrlAndToken,
  getFileWithBaseUrlAndToken,
  getWithBaseUrlAndToken,
} from '../fetchWithBaseUrlAndToken'
import { TenantFilesDto, ThemeJsonDto } from './model'

export function useAllTenantFiles(): FetchResult<TenantFilesDto[]> {
  const { data, error, mutate, isLoading } = useSWR<FetchResponse<TenantFilesDto[]>, FetchError>(
    '/branding',
    getWithBaseUrlAndToken,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
    etag: data?.etag,
  }
}

export function useAllTenantFilesAsTree(): FetchResult<TreeNode> {
  const { data, error, mutate, isLoading } = useSWR<FetchResponse<TreeNode>, FetchError>(
    '/branding/$tree',
    getWithBaseUrlAndToken,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
    etag: data?.etag,
  }
}

export function useThemeJson(path: string): FetchResult<ThemeJsonDto> {
  const url = path ? `/branding/content/?path=${path}` : null
  const { data, error, mutate, isLoading } = useSWR<FetchResponse<any>, FetchError>(
    url,
    () => getWithBaseUrlAndToken(url!, true),
    {
      shouldRetryOnError: false,
    },
  )

  let themeData: ThemeJsonDto | undefined = undefined

  // Convert data.body to proper ThemeJsonDto object
  if (data?.body) {
    try {
      // If the response is already a string, parse it
      if (typeof data.body === 'string') {
        themeData = JSON.parse(data.body) as ThemeJsonDto
      }
      // If it's already an object but might need sanitizing
      else {
        // Clean conversion through stringify/parse cycle
        themeData = JSON.parse(JSON.stringify(data.body)) as ThemeJsonDto
      }
    } catch (err) {
      console.error('Error parsing theme JSON data:', err)
      // Return undefined which will be handled as an error state
    }
  }

  return {
    data: themeData,
    isLoading,
    error,
    mutate,
    etag: data?.etag,
  }
}

export function useFileContent(path: string | null): FetchResult<any> {
  const url = path ? `/branding/content/?path=${path}` : null
  const { data, error, mutate, isLoading } = useSWR<FetchResponse<any>, FetchError>(
    url,
    getContentWithBaseUrlAndToken,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
    etag: data?.etag,
  }
}

export function useFile(path: string | null): FetchResult<any> {
  const url = path ? `/branding/download/?path=${encodeURIComponent(path)}` : null
  const { data, error, mutate, isLoading } = useSWR<FetchResponse<any>, FetchError>(
    url,
    getFileWithBaseUrlAndToken,
  )

  return {
    data: data,
    isLoading,
    error,
    mutate,
    etag: data?.etag,
  }
}
